import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'

import {
  useDELETE_announcementMutation,
  useGET_announcementByIdQuery,
  usePATCH_announcementMutation,
  usePOST_announcementMutation,
} from 'redux/api/announcementsApi'

import { useGET_courseSelectQuery } from 'redux/api/coursesApi'
import { useGET_groupSelectQuery } from 'redux/api/groupsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/announcementModel'
import { crudColorMap, crudIconMap, toHtml } from 'utils/helpers'

import { ActionType, IAnnouncementData } from 'types'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const AnnouncementForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = '/announcements'

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.announcements')}`,
    entityId: id,
    getEntityQuery: useGET_announcementByIdQuery,
    createEntityMutation: usePOST_announcementMutation,
    editEntityMutation: usePATCH_announcementMutation,
    deleteEntityMutation: useDELETE_announcementMutation,
  })
  const { text, groups, group_courses, is_staff_only, is_broadcast } = formModel()

  const serverValues = entity?.id
    ? {
        ...entity,
        groups_ids: entity.groups.map((group) => group.id),
        group_courses_ids: entity.group_courses.map((course) => course.id),
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IAnnouncementData = {
          ...values,
          groups_ids: values.groups.map((group) => group.id),
          group_courses_ids: values.group_courses.map((course) => course.id),
        }
        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>

          {
            action === 'view' && 
            <Grid item xs={12}><div dangerouslySetInnerHTML={toHtml(entity?.text ?? '')}></div></Grid>
          }
          { action !== 'view' && <>
          <Grid item xs={12}>
            <SelectField
              multiple={true}
              fieldProps={{ ...groups }}
              apiCall={useGET_groupSelectQuery}
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <RichTextField
              fieldProps={{ ...text }}
              disabled={viewOnly}
              label={`${t('common.description')}`}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxField fieldProps={{ ...is_staff_only }} disabled={viewOnly} />
            <CheckboxField fieldProps={{ ...is_broadcast }} disabled={viewOnly} />
          </Grid>
          </>}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>

              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default AnnouncementForm
